import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { IMG_DAINASOUL_IMG2 as BottomImage1 } from "./imageConfig";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  (process.env.NODE_ENV === "development" && "http://localhost:8000") ||
  "";

const EmailPopup = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    const shown = localStorage.getItem("dainasoul_style_popup");
    if (!shown) {
      setTimeout(() => setOpen(true), 1000);
    }
  }, []);

  const handleClose = () => {
    setOpen(false);
    localStorage.setItem("dainasoul_style_popup", "true");
  };

  const handleSubmit = async () => {
    if (!email.trim()) {
      setIsError(true);
      setMessage("Please specify an email address");
      return;
    }

    try {
      // Trigger Welcome Email through our backend
      const response = await fetch(`${BACKEND_URL}/api/email/welcome`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error("Failed to send welcome email");
      }

      setIsError(false);
      setMessage("Welcome email sent! Check your inbox.");
      setTimeout(() => {
        handleClose();
      }, 3000);
    } catch (error) {
      console.error("Error subscribing:", error);
      setIsError(true);
      setMessage("Oops! Something went wrong. Try again.");
    }
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: isMobile ? "90%" : "80%",
    maxWidth: 900,
    minHeight: isMobile ? "auto" : 320,
    bgcolor: "#fff",
    borderRadius: 0,
    overflow: "hidden",
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    boxSizing: "border-box",
  };

  const imageSectionStyle = {
    width: isMobile ? "100%" : "50%",
    height: isMobile ? 200 : "100%",
    minHeight: isMobile ? 200 : 320,
    backgroundImage: `url(${BottomImage1})`,
    backgroundSize: "cover",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    display: "flex",
    boxSizing: "border-box",
  };

  const contentSectionStyle = {
    p: isMobile ? 2 : isTablet ? 3 : 5,
    pb: isMobile ? 6 : isTablet ? 4 : 6, // Increased bottom padding
    position: "relative",
    width: isMobile ? "100%" : "50%",
    background: "linear-gradient(135deg, #fff, #f9f9f9)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    boxSizing: "border-box",
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyle}>
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            zIndex: 10,
            bgcolor: "rgba(255,255,255,0.7)",
            color: "#000",
            "&:hover": { bgcolor: "#fff" },
          }}
        >
          <CloseIcon fontSize={isMobile ? "small" : "medium"} />
        </IconButton>

        {/* Left Image Section */}
        <Box sx={imageSectionStyle} />

        {/* Right Content Section */}
        <Box sx={contentSectionStyle}>

          <Typography
            variant={isMobile ? "h6" : "h5"}
            sx={{
              fontWeight: "bold",
              mb: isMobile ? 1 : 2,
              color: "#111",
              lineHeight: 1.4,
              fontSize: isMobile ? "1.1rem" : "inherit",
            }}
          >
            Want 10% OFF Your Order?
          </Typography>

          <Typography
            variant="body1"
            sx={{
              mb: isMobile ? 2 : 4,
              color: "#444",
              fontSize: isMobile ? "0.85rem" : "14px",
              lineHeight: 1.6,
            }}
          >
            Join the Dainasoul community and receive 10% off your next purchase.
            Be the first to hear about new drops, sales & exclusive discounts.
          </Typography>

          <TextField
            fullWidth
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            variant="outlined"
            size="small" // Consistent small size for all devices
            sx={{
              bgcolor: "#fff",
              borderRadius: 2,
              boxShadow: "0px 2px 8px rgba(0,0,0,0.05)",
              width: "100%",
              "& .MuiOutlinedInput-root": {
                pr: 0, // Remove right padding to maximize space for button
              },
              "& .MuiInputBase-input": {
                pr: isMobile ? 1 : 2, // Adjust input padding right
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ mr: isMobile ? 0.1 : 0 }}>
                  <IconButton
                    onClick={handleSubmit}
                    disableRipple
                    sx={{
                      bgcolor: "#000",
                      color: "#fff",
                      "&:hover": {
                        bgcolor: "#222",
                      },
                      borderRadius: 0,
                      p: isMobile ? "8px" : "12px",
                      ml: 0.5,
                      width: isMobile ? 48 : 80,
                      height: "100%",
                      minHeight: 36,
                      transition: "all 0.2s ease",
                    }}
                  >
                    <ArrowForwardIcon
                      fontSize={isMobile ? "small" : "medium"}
                      sx={{ fontSize: isMobile ? 18 : 24 }}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {message && (
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                p: 1,
                backgroundColor: isError ? "#fdecea" : "#e7f6e7",
                color: isError ? "#b71c1c" : "#1b5e20",
                borderRadius: 0,
              }}
            >
              {message}
            </Typography>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default EmailPopup;
